import APIConfig from "service/network/APIConfig";
import APIService from "service/network/APIService";

/**
 * Activity v2 API client (api-activity-v2-service). Mirrors the earlier v2
 * clients — five methods here since this section has both a full-detail
 * Update and a status-only Update (like Medicine v2 / Addiction v2).
 *
 * Contract notes:
 *
 * 1. Every mutating call needs `expectedVersion` (the last version the caller
 *    saw). On mismatch the server returns 409 with a fresh doc.
 *
 * 2. `catalogId` + `category` are optional catalog snapshots. Free-text
 *    activities (no catalog pick) send empty strings; server stores them
 *    verbatim and they display without a category tag.
 *
 * 3. Status enum: "active" | "occasional" | "inactive".
 *    Intensity enum: "light" | "moderate" | "vigorous".
 *
 * 4. `statusHistory` is server-side-only for compliance; not returned in
 *    responses. No trend endpoint here by design.
 *
 * 5. Delete is soft — server flips deleted=true; the row disappears from
 *    subsequent list responses.
 */
class ActivityService {
  constructor() {
    this.apiService = new APIService();
  }

  list(patientId, onResponse, onError) {
    const url = APIConfig.activityV2.list.format(patientId);
    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * Add an activity. Body shape matches beans.AddRequest:
   *   { name, category?, catalogId?, daysPerWeek, minutesPerSession,
   *     intensity, status, description?, expectedVersion }
   */
  add(patientId, body, onResponse, onError) {
    const url = APIConfig.activityV2.add.format(patientId);
    return this.apiService.post(url, body, onResponse, onError);
  }

  /**
   * Full-detail edit. Same body shape as add; server preserves AddedBy/AddedAt.
   */
  update(patientId, activityId, body, onResponse, onError) {
    const url = APIConfig.activityV2.update.format(patientId, activityId);
    return this.apiService.post(url, body, onResponse, onError);
  }

  /**
   * Status-only update. Body shape matches beans.UpdateStatusRequest:
   *   { status, statusUpdate?, description?, expectedVersion }
   */
  updateStatus(patientId, activityId, body, onResponse, onError) {
    const url = APIConfig.activityV2.updateStatus.format(patientId, activityId);
    return this.apiService.post(url, body, onResponse, onError);
  }

  /**
   * Trend — per-activity audit history with actor names enriched.
   */
  trend(patientId, activityId, onResponse, onError) {
    const url = APIConfig.activityV2.trend.format(patientId, activityId);
    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * Mark one history row as incorrect. Audit-only — live Activity
   * unchanged. Body: { expectedVersion }.
   */
  markHistoryIncorrect(patientId, activityId, index, expectedVersion, onResponse, onError) {
    const url = APIConfig.activityV2.markHistoryIncorrect.format(
      patientId,
      activityId,
      index
    );
    return this.apiService.post(
      url,
      { expectedVersion: expectedVersion || 0 },
      onResponse,
      onError
    );
  }

  /**
   * Soft-delete. Body carries `expectedVersion` for optimistic locking.
   * APIService has no dedicated DELETE helper — direct fetch like Medicine
   * v2 and Family History v2.
   */
  delete(patientId, activityId, expectedVersion, onResponse, onError) {
    const url = APIConfig.activityV2.delete.format(patientId, activityId);
    return fetch(url, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        client: this._client(),
        phone_no: this._header("phone_no"),
        auth_token: this._header("auth_token"),
      },
      body: JSON.stringify({ expectedVersion: expectedVersion || 0 }),
    })
      .then((r) => r.json().then((data) => (r.ok ? onResponse(data) : onError(data))))
      .catch((err) =>
        onError({ message: err && err.message ? err.message : "Delete failed" })
      );
  }

  _header(name) {
    try {
      const user = JSON.parse(localStorage.getItem("zy.USER_PROFILE"));
      if (name === "phone_no") return (user && user.phoneno) || "";
      if (name === "auth_token") {
        return (user && user.accessToken && user.accessToken.accessToken) || "";
      }
    } catch (e) {
      /* localStorage unavailable — return empty and let the server 401 */
    }
    return "";
  }

  _client() {
    try {
      const user = JSON.parse(localStorage.getItem("zy.USER_PROFILE"));
      const isDoctor =
        user && user.doctorProfile && user.doctorProfile.id > 0 &&
        !(user.employeeProfile && user.employeeProfile.id > 0);
      return isDoctor ? "doctor_web" : "bridge_web";
    } catch (e) {
      return "bridge_web";
    }
  }
}

export default ActivityService;
